import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../utils/api';

import { getDriveApiToken } from '../../lib/auth-token';
export interface CloudConnection {
  id: string;
  provider: string;
  name: string;
  accountEmail: string;
  accountName?: string;
  status: 'connected' | 'error' | 'disconnected';
  usedSpace: number;
  totalSpace: number;
  syncEnabled: boolean;
  lastSync?: string;
  expiresAt?: string;
}

interface CloudState {
  connections: CloudConnection[];
  loading: boolean;
  error: string | null;
}

const initialState: CloudState = {
  connections: [],
  loading: false,
  error: null,
};

// Helper to configure axios with token
const getAxiosConfig = () => {
  const token = getDriveApiToken();
  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
};

// Async actions
export const fetchCloudConnections = createAsyncThunk(
  'cloud/fetchConnections',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/cloud/connections', getAxiosConfig());
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch cloud connections');
    }
  }
);

export const addCloudConnection = createAsyncThunk(
  'cloud/addConnection',
  async (provider: string, { rejectWithValue }) => {
    try {
      const response = await api.post('/cloud/connections', { provider }, getAxiosConfig());
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add cloud connection');
    }
  }
);

export const removeCloudConnection = createAsyncThunk(
  'cloud/removeConnection',
  async (connectionId: string, { rejectWithValue }) => {
    try {
      await api.delete(`/cloud/connections/${connectionId}`, getAxiosConfig());
      return connectionId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to remove cloud connection');
    }
  }
);

export const refreshCloudConnection = createAsyncThunk(
  'cloud/refreshConnection',
  async (connectionId: string, { rejectWithValue }) => {
    try {
      const response = await api.post(`/cloud/connections/${connectionId}/refresh`, {}, getAxiosConfig());
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to refresh cloud connection');
    }
  }
);

const cloudSlice = createSlice({
  name: 'cloud',
  initialState,
  reducers: {
    clearCloudError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Cloud Connections
      .addCase(fetchCloudConnections.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCloudConnections.fulfilled, (state, action: PayloadAction<CloudConnection[]>) => {
        state.loading = false;
        state.connections = action.payload;
      })
      .addCase(fetchCloudConnections.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Add Cloud Connection
      .addCase(addCloudConnection.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addCloudConnection.fulfilled, (state, action: PayloadAction<CloudConnection>) => {
        state.loading = false;
        state.connections.push(action.payload);
      })
      .addCase(addCloudConnection.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Remove Cloud Connection
      .addCase(removeCloudConnection.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeCloudConnection.fulfilled, (state, action: PayloadAction<string>) => {
        state.loading = false;
        state.connections = state.connections.filter(
          (connection) => connection.id !== action.payload
        );
      })
      .addCase(removeCloudConnection.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Refresh Cloud Connection
      .addCase(refreshCloudConnection.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(refreshCloudConnection.fulfilled, (state, action: PayloadAction<CloudConnection>) => {
        state.loading = false;
        const index = state.connections.findIndex(
          (connection) => connection.id === action.payload.id
        );
        if (index !== -1) {
          state.connections[index] = action.payload;
        }
      })
      .addCase(refreshCloudConnection.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearCloudError } = cloudSlice.actions;
export default cloudSlice.reducer; 